import { type ReactNode, useCallback, useState } from "react";
import {
  orderInspectorTabs,
  resolveActiveSlot,
  slotLabel,
  tabStorageKey,
  type InspectorSlot,
  type InspectorTabDef,
} from "./inspectorModel.js";

export type InspectorTabsProps = {
  surfaceId: string;
  /** Keys last-active persistence — typically the node type (e.g. "transform"). */
  tabGroupId: string;
  /** Caller passes only the populated slots, in any order; the rack sorts them. */
  tabs: InspectorTabDef<ReactNode>[];
  /**
   * Controlled mode: when both are provided, the parent owns the active slot
   * (e.g. an inspector whose existing tab state must stay the single source of
   * truth for downstream effects). Otherwise the rack manages + persists it.
   */
  activeSlot?: InspectorSlot;
  onSlotChange?: (slot: InspectorSlot) => void;
  /**
   * Uncontrolled mode only: the slot to open on when there's no valid persisted
   * choice, overriding the first-populated-slot fallback. Lets a node land on
   * Setup (configure-first) even though Info leads the canonical order.
   */
  defaultSlot?: InspectorSlot;
};

/**
 * The fixed five-slot tab rack (ADR-0129 §3.2). Renders only the populated
 * slots in canonical order; remembers the last-active slot per tab group;
 * keeps every panel mounted and toggles inactive ones with `hidden` so
 * in-flight chats/previews survive tab switches (§3.5).
 *
 * Content remounts on node switch is the caller's responsibility — key this
 * component (or its parent subtree) by the selected node's id.
 */
export function InspectorTabs({ surfaceId, tabGroupId, tabs, activeSlot, onSlotChange, defaultSlot }: InspectorTabsProps) {
  const ordered = orderInspectorTabs(tabs);
  const available = ordered.map((t) => t.slot);
  const storageKey = tabStorageKey(surfaceId, tabGroupId);
  const controlled = activeSlot !== undefined && onSlotChange !== undefined;

  const [active, setActive] = useState<InspectorSlot | null>(() => {
    let persisted: string | null = null;
    try {
      persisted = localStorage.getItem(storageKey);
    } catch {
      /* localStorage unavailable */
    }
    return resolveActiveSlot(persisted, available, defaultSlot);
  });

  // Controlled: honor the parent's slot. Uncontrolled: internal state, falling
  // back to the first available slot when the current one isn't populated.
  const current = controlled ? activeSlot! : active;
  const effectiveActive: InspectorSlot | null =
    current && available.includes(current) ? current : available[0] ?? null;

  const select = useCallback(
    (slot: InspectorSlot) => {
      if (controlled) {
        onSlotChange!(slot);
        return;
      }
      setActive(slot);
      try {
        localStorage.setItem(storageKey, slot);
      } catch {
        /* ignore persistence failure */
      }
    },
    [controlled, onSlotChange, storageKey],
  );

  if (ordered.length === 0) return null;

  return (
    <div className="inspectorTabRack">
      <div className="inspectorTabRackBar tabs" role="tablist">
        {ordered.map((t) => {
          const isActive = t.slot === effectiveActive;
          return (
            <button
              key={t.slot}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`tab${isActive ? " tabActive" : ""}`}
              onClick={() => select(t.slot)}
            >
              {slotLabel(t.slot, t.label)}
              {t.badge != null ? <span className="inspectorTabBadge">{t.badge}</span> : null}
            </button>
          );
        })}
      </div>
      {ordered.map((t) => (
        <div
          key={t.slot}
          role="tabpanel"
          data-slot={t.slot}
          className={`inspectorTabRackPanel${t.fill ? " inspectorTabRackPanelFill" : ""}`}
          hidden={t.slot !== effectiveActive}
        >
          {t.content}
        </div>
      ))}
    </div>
  );
}
